import { ref } from 'vue';
import { DeploymentRequestsHubConnection } from '@/services/DeploymentRequestsHubConnection';
import { useToastStore } from '@/stores/toastStore';
import { useNotificationSettingsStore } from '@/stores/notificationSettingsStore';
import { DeploymentRequestDto, DeploymentStatus, AutomationTestJobDto, AutomationTestJobStatus } from '@/api/models';
import auth from '@/services/Auth';

export function useDeploymentToasts() {
  const toastStore = useToastStore();
  const notificationSettings = useNotificationSettingsStore();
  let hubConnection: DeploymentRequestsHubConnection | null = null;
  const isInitialized = ref(false);

  // Track previous statuses to detect transitions
  const previousStatuses = new Map<number, DeploymentStatus>();
  const previousTestStatuses = new Map<number, AutomationTestJobStatus>();
  // Track which deployments belong to the current user (for test job notifications)
  const userDeploymentIds = new Set<number>();
  // Track branch names by deployment ID (for test job notifications)
  const deploymentBranchNames = new Map<number, string>();

  const isOwnDeployment = (deployment: DeploymentRequestDto): boolean => {
    return deployment.requestedById === auth.account?.localAccountId;
  };

  const formatBranchName = (branchName?: string): string => {
    if (!branchName) return 'Unknown';
    const parts = branchName.split('/');
    return parts[parts.length - 1];
  };

  const onDeploymentRequestUpdated = (deployment: DeploymentRequestDto) => {
    const previousStatus = previousStatuses.get(deployment.deploymentRequestId);
    const currentStatus = deployment.status;

    // Update tracked status
    previousStatuses.set(deployment.deploymentRequestId, currentStatus!);

    // Track branch name for test job notifications
    if (deployment.branchName) {
      deploymentBranchNames.set(deployment.deploymentRequestId, deployment.branchName);
    }

    // Track user's deployments for test job notifications
    if (isOwnDeployment(deployment)) {
      userDeploymentIds.add(deployment.deploymentRequestId);
    }

    // Skip if this is the first time we're seeing this deployment
    if (previousStatus === undefined) return;

    // Skip if status hasn't changed
    if (previousStatus === currentStatus) return;

    // Only show notifications for user's own deployments (unless scope is 'all')
    if (notificationSettings.scope === 'own' && !isOwnDeployment(deployment)) return;

    const branchName = formatBranchName(deployment.branchName);

    // Show toast based on status transition
    switch (currentStatus) {
      case DeploymentStatus.Deployed:
        toastStore.success(
          'Deployment Complete',
          `${branchName} has been deployed successfully`,
          deployment.deploymentRequestId
        );
        break;

      case DeploymentStatus.Error:
        toastStore.error(
          'Deployment Failed',
          `${branchName} encountered an error`,
          deployment.deploymentRequestId
        );
        break;

      case DeploymentStatus.Destroyed:
        toastStore.info(
          'Deployment Destroyed',
          `${branchName} has been destroyed`,
          deployment.deploymentRequestId
        );
        break;

      case DeploymentStatus.DestroyedError:
        toastStore.warning(
          'Deployment Force Cleaned',
          `${branchName} was manually cleaned up after an error`,
          deployment.deploymentRequestId
        );
        break;

      case DeploymentStatus.Deploying:
        if (previousStatus === DeploymentStatus.PendingDeployment) {
          toastStore.info(
            'Deployment Started',
            `${branchName} is now deploying`,
            deployment.deploymentRequestId
          );
        }
        break;

      case DeploymentStatus.Paused:
        toastStore.warning(
          'Deployment Paused',
          `${branchName} has been paused`,
          deployment.deploymentRequestId
        );
        break;
    }
  };

  const onDeploymentRequestInserted = (deployment: DeploymentRequestDto) => {
    // Track initial status
    previousStatuses.set(deployment.deploymentRequestId, deployment.status!);

    // Track branch name for test job notifications
    if (deployment.branchName) {
      deploymentBranchNames.set(deployment.deploymentRequestId, deployment.branchName);
    }

    // Track user's deployments for test job notifications
    if (isOwnDeployment(deployment)) {
      userDeploymentIds.add(deployment.deploymentRequestId);
    }

    // Show notification based on scope setting
    const shouldNotify = notificationSettings.scope === 'all' || isOwnDeployment(deployment);
    if (shouldNotify) {
      const branchName = formatBranchName(deployment.branchName);
      toastStore.info(
        'New Deployment',
        `${branchName} deployment requested`,
        deployment.deploymentRequestId
      );
    }
  };

  const isOwnTestJob = (testJob: AutomationTestJobDto): boolean => {
    // Check if the test is for a deployment owned by the current user
    return userDeploymentIds.has(testJob.parentDeploymentRequestId) ||
           userDeploymentIds.has(testJob.deploymentRequestId);
  };

  const getTestJobBranchName = (testJob: AutomationTestJobDto): string => {
    // Try to get branch name from parent deployment first, then from the test's deployment
    const branchName = deploymentBranchNames.get(testJob.parentDeploymentRequestId) ||
                       deploymentBranchNames.get(testJob.deploymentRequestId);
    return formatBranchName(branchName);
  };

  const onAutomationTestJobUpdated = (testJob: AutomationTestJobDto) => {
    const previousStatus = previousTestStatuses.get(testJob.automationTestJobId);
    const currentStatus = testJob.status;

    // Update tracked status
    previousTestStatuses.set(testJob.automationTestJobId, currentStatus!);

    // Skip if this is the first time we're seeing this test
    if (previousStatus === undefined) return;

    // Skip if status hasn't changed
    if (previousStatus === currentStatus) return;

    // Only show notifications for user's own test jobs (unless scope is 'all')
    if (notificationSettings.scope === 'own' && !isOwnTestJob(testJob)) return;

    const testTag = testJob.testTag || 'All Tests';
    const branchName = getTestJobBranchName(testJob);

    switch (currentStatus) {
      case AutomationTestJobStatus.Complete:
        toastStore.success(
          'Tests Completed',
          `${testTag} on ${branchName} finished successfully`
        );
        break;

      case AutomationTestJobStatus.Error:
        toastStore.error(
          'Tests Failed',
          `${testTag} on ${branchName} encountered an error`
        );
        break;

      case AutomationTestJobStatus.RunningTests:
        if (previousStatus === AutomationTestJobStatus.WaitingForDeployment) {
          toastStore.info(
            'Tests Running',
            `${testTag} on ${branchName} are now running`
          );
        }
        break;
    }
  };

  const onAutomationTestJobInserted = (testJob: AutomationTestJobDto) => {
    previousTestStatuses.set(testJob.automationTestJobId, testJob.status!);
  };

  const initialize = async () => {
    if (isInitialized.value) return;

    // Wait for authentication before connecting to SignalR
    if (!auth.isAuthenticated) {
      // Retry after a delay if not yet authenticated
      setTimeout(() => initialize(), 2000);
      return;
    }

    try {
      hubConnection = new DeploymentRequestsHubConnection();
      await hubConnection.init();

      hubConnection.addDeploymentRequestUpdatedListener(onDeploymentRequestUpdated);
      hubConnection.addDeploymentRequestInsertedListener(onDeploymentRequestInserted);
      hubConnection.addAutomationTestJobUpdatedListener(onAutomationTestJobUpdated);
      hubConnection.addAutomationTestJobInsertedListener(onAutomationTestJobInserted);

      isInitialized.value = true;
    } catch (error) {
      console.error('Failed to initialize deployment toasts:', error);
    }
  };

  const cleanup = () => {
    if (hubConnection) {
      hubConnection.removeDeploymentRequestUpdatedListener(onDeploymentRequestUpdated);
      hubConnection.removeDeploymentRequestInsertedListener(onDeploymentRequestInserted);
      hubConnection.removeAutomationTestJobUpdatedListener(onAutomationTestJobUpdated);
      hubConnection.removeAutomationTestJobInsertedListener(onAutomationTestJobInserted);
      hubConnection = null;
    }
    previousStatuses.clear();
    previousTestStatuses.clear();
    userDeploymentIds.clear();
    deploymentBranchNames.clear();
    isInitialized.value = false;
  };

  return {
    initialize,
    cleanup,
    isInitialized
  };
}
